import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Modal, Button, Table } from "react-bootstrap";
import Select, { components } from "react-select";
import Swal from "sweetalert2";
import CashDocumentPreviewModal, { getCashDocuments } from "./CashDocumentPreviewModal";

import CashEntryServices from "../../../services/CashEntryServices";
import Pagination from "../Pagenation/Pagenation";
import Loader from "../Loader/loader";

const pendingStatuses = ["pendingAdmin", "pendingAccounts"];

const statusLabels = {
  pendingAdmin: "CashChecker",
  pendingAccounts: "Accounts"
};

const readEntries = (response) => {
  const body = response?.data?.status ? response.data : response;

  if (!Array.isArray(body?.data?.entries)) {
    throw new Error("Unexpected cash entry API response.");
  }

  return body.data.entries;
};

const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    return user?.data?.user?.role || "";
  } catch {
    return "";
  }
};

const toInitCap = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) =>
  getAmount(value).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};
const getLogPerson = (person, fallback = "—") =>
  person && typeof person === "object"
    ? person.name || fallback
    : fallback;

const formatLogDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const getEntryLogs = (entry) => {
  if (!entry) return [];

  const logs = [];

  (entry.rejectionReprocessHistory || []).forEach((history) => {
    logs.push({
      event: "Rejected Entry Reprocessed for Approval",
      date: history.reprocessedAt,
      by: getLogPerson(history.reprocessedBy, "CashMaker"),
      remark: history.reason || "—"
    });
  });

  if (entry.returnedAt) {
    logs.push({
      event: entry.returnWasBypass
        ? "Returned to CashMaker — Accounts Bypass"
        : "Returned to CashMaker",
      date: entry.returnedAt,
      by: getLogPerson(
        entry.returnedBy,
        entry.returnedFrom || "—"
      ),
      remark: entry.returnReason || "—"
    });
  }

  if (entry.resubmittedAt) {
    logs.push({
      event: "Corrected & Resubmitted",
      date: entry.resubmittedAt,
      by: getLogPerson(
        entry.resubmittedBy,
        entry.employee?.name || "CashMaker"
      ),
      remark: "Resubmitted for approval."
    });
  }

  return logs.sort(
    (first, second) =>
      new Date(first.date).getTime() -
      new Date(second.date).getTime()
  );
};

const getISTDateKey = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);

  const getPart = (type) =>
    parts.find((part) => part.type === type)?.value || "";

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
};
const CashMakerOption = (props) => (
  <components.Option {...props}>
    <span className="d-flex align-items-center gap-2">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex={-1}
        className="pe-none"
        aria-hidden="true"
      />
      <span>{props.label}</span>
    </span>
  </components.Option>
);

const CashMakerValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const [values, input] = children;

  return (
    <components.ValueContainer {...props}>
      {selected.length > 0 ? (
        <span
          className="text-truncate"
          title={selected.map((option) => option.label).join(", ")}
        >
          {selected[0].label}
          {selected.length > 1 ? ` +${selected.length - 1}` : ""}
        </span>
      ) : (
        values
      )}
      {input}
    </components.ValueContainer>
  );
};

const CashEntryPendingModal = ({
  show,
  handleClose,
  userRole = ""
}) => {
  const role = String(userRole || getUserRole())
    .trim()
    .toLowerCase();

  const allowed = [
    "cashmaker",
    "cashchecker",
    "accounts"
  ].includes(role);

  const canReview = ["cashchecker", "accounts"].includes(role);

  const reviewStatus =
    role === "cashchecker"
      ? "pendingAdmin"
      : role === "accounts"
        ? "pendingAccounts"
        : "";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [documentEntry, setDocumentEntry] = useState(null);
  const [logEntry, setLogEntry] = useState(null);

  useEffect(() => {
    if (!show) setDocumentEntry(null);
  }, [show]);

  const [activeTab, setActiveTab] = useState("in");
  const [selectedStore, setSelectedStore] = useState("");
  const [search, setSearch] = useState("");
  const [requestFlagFilter, setRequestFlagFilter] = useState("");
  const [selectedCashMaker, setSelectedCashMaker] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedAmounts, setSelectedAmounts] = useState([]);
  const [pendingWithFilter, setPendingWithFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [documentIndex, setDocumentIndex] = useState(0);
  const actionLock = useRef(false);
  const selectPageRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setActiveTab("in");
    setSelectedStore("");
    setSearch("");
    setRequestFlagFilter("");
    setSelectedCashMaker([]);
    setSelectedCategories([]);
    setSelectedAmounts([]);
    setPendingWithFilter("");
    setFromDate("");
    setToDate("");
    setPage(0);
    setSelectedIds([]);
  }, [show, role]);

  useEffect(() => {
    if (!show || !allowed) return;

    let cancelled = false;

    const loadEntries = async () => {
      setLoading(true);
      setLoadError("");
      setEntries([]);
      setSelectedIds([]);

      try {
        // Backend applies assigned-store access for Maker/Checker.
        // Accounts receives pending entries across all stores.
        const response = await CashEntryServices.getCashEntries({
          pendingOnly: true
        });

        const unique = new Map();

        readEntries(response).forEach((entry) => {
          if (
            entry?._id &&
            pendingStatuses.includes(entry.status)
          ) {
            unique.set(String(entry._id), entry);
          }
        });

        const result = [...unique.values()].sort((a, b) => {
          const first = new Date(a.createdAt || a.dateTime).getTime();
          const second = new Date(b.createdAt || b.dateTime).getTime();
          return (second || 0) - (first || 0);
        });

        if (!cancelled) setEntries(result);
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load pending cash entries."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEntries();

    return () => {
      cancelled = true;
    };
  }, [show, role, allowed, refreshKey]);

  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
  }, [
    activeTab,
    selectedStore,
    selectedCashMaker,
    selectedCategories,
    selectedAmounts,
    pendingWithFilter,
    fromDate,
    toDate,
    search,
    pageSize,
    requestFlagFilter
  ]);

  const storeOptions = useMemo(() => {
    const stores = new Map();

    entries.forEach((entry) => {
      const store = entry.store;

      if (store?._id) {
        stores.set(String(store._id), {
          value: String(store._id),
          label:
            [
              toInitCap(store.name),
              toInitCap(store.location)
            ]
              .filter(Boolean)
              .join(", ") || "Store"
        });
      }
    });

    return [
      {
        value: "",
        label: role === "accounts" ? "All Stores" : "All Assigned Stores"
      },
      ...[...stores.values()].sort((a, b) =>
        a.label.localeCompare(b.label)
      )
    ];
  }, [entries, role]);

  const cashMakerOptions = useMemo(() => {
    const makers = new Map();

    entries.forEach((entry) => {
      const storeId = String(entry.store?._id || entry.store || "");

      if (selectedStore && storeId !== selectedStore) return;

      const employee = entry.employee;
      const employeeId = String(employee?._id || employee || "");

      if (!employeeId) return;

      makers.set(employeeId, {
        value: employeeId,
        label: toInitCap(employee?.name) || employeeId
      });
    });

    return [...makers.values()].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [entries, selectedStore]);

  const { categoryOptions, amountOptions } = useMemo(() => {
    const categories = new Map();
    const amounts = new Map();

    entries.forEach((entry) => {
      const storeId = String(entry.store?._id || entry.store || "");

      if (selectedStore && storeId !== selectedStore) return;

      const category = entry.category;
      const categoryId = String(category?._id || category || "");

      if (categoryId) {
        categories.set(categoryId, {
          value: categoryId,
          label: category?.name || categoryId
        });
      }

      if (
        entry.amount !== null &&
        entry.amount !== undefined &&
        entry.amount !== "" &&
        Number.isFinite(Number(entry.amount))
      ) {
        const amount = Number(entry.amount);

        amounts.set(String(amount), {
          value: String(amount),
          label: formatAmount(amount)
        });
      }
    });

    return {
      categoryOptions: [...categories.values()].sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
      amountOptions: [...amounts.values()].sort(
        (a, b) => Number(a.value) - Number(b.value)
      )
    };
  }, [entries, selectedStore]);

  const scopedEntries = useMemo(() => {
    const term = search.trim().toLowerCase();

    return entries.filter((entry) => {
      if (
        pendingWithFilter &&
        entry.status !== pendingWithFilter
      ) {
        return false;
      }
      if (fromDate || toDate) {
        const entryDate = getISTDateKey(entry.dateTime);

        if (!entryDate) return false;
        if (fromDate && entryDate < fromDate) return false;
        if (toDate && entryDate > toDate) return false;
      }
      const storeId = String(
        entry.store?._id || entry.store || ""
      );

      if (selectedStore && storeId !== selectedStore) {
        return false;
      }
      const cashMakerId = String(
        entry.employee?._id || entry.employee || ""
      );

      if (
        selectedCashMaker.length > 0 &&
        !selectedCashMaker.includes(cashMakerId)
      ) {
        return false;
      }

      const categoryId = String(
        entry.category?._id || entry.category || ""
      );

      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(categoryId)
      ) {
        return false;
      }

      if (
        selectedAmounts.length > 0 &&
        (
          entry.amount === null ||
          entry.amount === undefined ||
          entry.amount === "" ||
          !selectedAmounts.includes(String(Number(entry.amount)))
        )
      ) {
        return false;
      }
      const currentRequestFlag =
        entry.requestFlag === "rejectedReprocess"
          ? "rejectedReprocess"
          : entry.requestFlag === "resubmitted" ||
            Number(entry.resubmissionCount || 0) > 0
            ? "resubmitted"
            : "new";

      if (
        requestFlagFilter &&
        currentRequestFlag !== requestFlagFilter
      ) {
        return false;
      }

      const text = [
        entry.requestNumber,
        entry.passbook?.name,
        entry.store?.name,
        entry.store?.location,
        entry.employee?.name,
        entry.category?.name,
        entry.vendor?.name,
        entry.vendor?.mobileNo,
        entry.voucherNo,
        entry.remark,
        entry.paymentMode,
        entry.amount,
        entry.pendingWith,
        statusLabels[entry.status]
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase();

      return !term || text.includes(term);
    });
  }, [
    entries,
    selectedStore,
    selectedCashMaker,
    selectedCategories,
    selectedAmounts,
    pendingWithFilter,
    fromDate,
    toDate,
    search,
    requestFlagFilter
  ]);

  const cashInEntries = scopedEntries.filter(
    (entry) => entry.entryType === "in"
  );

  const cashOutEntries = scopedEntries.filter(
    (entry) => entry.entryType === "out"
  );

  const filteredEntries =
    activeTab === "in" ? cashInEntries : cashOutEntries;

  const pendingAmount = filteredEntries.reduce(
    (total, entry) => total + getAmount(entry.amount),
    0
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEntries.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * pageSize;

  const visibleEntries = filteredEntries.slice(
    start,
    start + pageSize
  );

  const needsAccountsBypass = (entry) =>
    role === "accounts" &&
    entry.entryType === "out" &&
    entry.status === "pendingAdmin";

  const canSelectEntry = (entry) =>
    canReview &&
    (
      entry.status === reviewStatus ||
      needsAccountsBypass(entry)
    );

  const selectedSet = new Set(selectedIds);

  const selectedEntries = filteredEntries.filter(
    (entry) =>
      canSelectEntry(entry) &&
      selectedSet.has(String(entry._id))
  );

  const bypassSelectedCount = selectedEntries.filter(
    needsAccountsBypass
  ).length;

  const selectedAmount = selectedEntries.reduce(
    (total, entry) => total + getAmount(entry.amount),
    0
  );

  const pageIds = visibleEntries
    .filter(canSelectEntry)
    .map((entry) => String(entry._id));

  const selectedPageCount = pageIds.filter(
    (id) => selectedSet.has(id)
  ).length;

  const allPageSelected =
    pageIds.length > 0 && selectedPageCount === pageIds.length;

  useEffect(() => {
    if (selectPageRef.current) {
      selectPageRef.current.indeterminate =
        selectedPageCount > 0 && !allPageSelected;
    }
  }, [selectedPageCount, allPageSelected, canReview, show]);

  const toggleEntry = (entry) => {
    if (
      loading ||
      actionLock.current ||
      !canSelectEntry(entry)
    ) {
      return;
    }

    const id = String(entry._id);

    setSelectedIds((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id]
    );
  };

  const togglePage = () => {
    if (
      loading ||
      actionLock.current ||
      !canReview ||
      !pageIds.length
    ) {
      return;
    }

    setSelectedIds((previous) => {
      const next = new Set(previous);
      const allSelected = pageIds.every((id) => next.has(id));

      pageIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });

      return [...next];
    });
  };

  const handlePageChange = (nextPage) => {
    if (loading || actionLock.current) return;

    const value = Number(nextPage);
    if (!Number.isInteger(value)) return;

    setPage(Math.min(Math.max(0, value), totalPages - 1));
  };

  const closeModal = () => {
    if (!actionLock.current) handleClose();
  };

  const reviewSelected = async (action) => {
    if (
      actionLock.current ||
      loading ||
      !canReview ||
      selectedEntries.length === 0
    ) {
      return;
    }

    const selected = [...selectedEntries];
    const bypassCount = selected.filter(needsAccountsBypass).length;
    const requiresRemark = action === "reject" || bypassCount > 0;
    actionLock.current = true;
    setProcessing(true);

    try {
      const confirmation = await Swal.fire({
        title:
          action === "approve"
            ? `Approve ${selected.length} entries?`
            : `Reject ${selected.length} entries?`,
        text:
          `Selected amount: ${formatAmount(selectedAmount)}.` +
          (
            bypassCount > 0
              ? ` ${bypassCount} entries will bypass CashChecker.` +
              (
                action === "approve"
                  ? " Approval completes both stages and updates the passbook."
                  : " Rejection leaves the passbook balance unchanged."
              )
              : ""
          ),
        icon: "warning",
        input: "textarea",
        inputLabel:
          bypassCount > 0
            ? "Bypass / action reason (required)"
            : action === "reject"
              ? "Rejection remark (required)"
              : "Approval remark (optional)",
        inputPlaceholder: "Enter remark",
        showCancelButton: true,
        confirmButtonText:
          action === "approve" ? "Yes, approve" : "Yes, reject",
        cancelButtonText: "Cancel",
        confirmButtonColor:
          action === "approve" ? "#198754" : "#dc3545",
        allowOutsideClick: false,
        inputValidator: (value) =>
          requiresRemark && !String(value || "").trim()
            ? "Please enter a reason."
            : undefined
      });

      if (!confirmation.isConfirmed) return;

      const remark = String(confirmation.value || "").trim();
      const successfulIds = [];
      const failures = [];

      // Existing review endpoints process one entry per request.
      for (const entry of selected) {
        try {
          const body = { action, remark };

          if (role === "cashchecker") {
            await CashEntryServices.reviewByAdmin(entry._id, body);
          } else {
            await CashEntryServices.reviewByAccounts(entry._id, {
              ...body,
              bypassChecker: needsAccountsBypass(entry)
            });
          }

          successfulIds.push(String(entry._id));
        } catch (error) {
          failures.push({
            id: String(entry._id),
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Unable to review this entry."
          });
        }
      }

      const completed = new Set(successfulIds);

      setEntries((previous) =>
        previous.flatMap((entry) => {
          if (!completed.has(String(entry._id))) {
            return [entry];
          }

          // Checker approval moves the entry to Accounts.
          // Keep it visible because it is still pending.
          if (role === "cashchecker" && action === "approve") {
            return [{
              ...entry,
              status: "pendingAccounts",
              pendingWith: "Accounts"
            }];
          }

          return [];
        })
      );

      setSelectedIds([]);

      // Refresh immediately after processing the actions.
      setRefreshKey((value) => value + 1);

      if (failures.length > 0) {
        await Swal.fire({
          icon: successfulIds.length ? "warning" : "error",
          title: successfulIds.length
            ? "Partially Completed"
            : "Review Failed",
          text:
            `${successfulIds.length} succeeded; ` +
            `${failures.length} failed. ` +
            `First error: ${failures[0].message}`,
          confirmButtonText: "OK",
          allowOutsideClick: false
        });
      } else {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            action === "approve"
              ? role === "accounts"
                ? `${successfulIds.length} entries approved and passbook balances updated.`
                : `${successfulIds.length} entries sent for Accounts approval.`
              : `${successfulIds.length} entries rejected.`,
          confirmButtonText: "OK",
          allowOutsideClick: false
        });
      }

    } catch (error) {
      setRefreshKey((value) => value + 1);

      await Swal.fire(
        "Error",
        error?.message || "Unable to complete review.",
        "error"
      );
    } finally {
      actionLock.current = false;
      setProcessing(false);
    }
  };

  const returnToCashMaker = async (
    items,
    bypassChecker = false
  ) => {
    if (
      loading ||
      actionLock.current ||
      !canReview ||
      !Array.isArray(items) ||
      items.length === 0
    ) {
      return;
    }

    const validSelection = items.every((entry) => {
      if (bypassChecker) {
        return needsAccountsBypass(entry);
      }

      if (role === "cashchecker") {
        return (
          entry.entryType === "out" &&
          entry.status === "pendingAdmin"
        );
      }

      return (
        role === "accounts" &&
        (
          (
            ["in", "out"].includes(entry.entryType) &&
            entry.status === "pendingAccounts"
          ) ||
          needsAccountsBypass(entry)
        )
      );
    });

    const returnBypassCount = items.filter(
      needsAccountsBypass
    ).length;

    if (!validSelection) {
      await Swal.fire(
        "Invalid Selection",
        "Select entries pending at your review stage.",
        "warning"
      );
      return;
    }

    actionLock.current = true;
    setProcessing(true);

    try {
      const confirmation = await Swal.fire({
        title: bypassChecker
          ? "Bypass & Return to CashMaker?"
          : `Return ${items.length} entries to CashMaker?`,
        text:
          "The passbook balance will remain unchanged." +
          (
            returnBypassCount > 0
              ? ` ${returnBypassCount} entries will bypass CashChecker and return to CashMaker.`
              : ""
          ),
        icon: "warning",
        input: "textarea",
        inputLabel: returnBypassCount > 0
          ? "Bypass / return reason (required)"
          : "Return reason (required)",
        inputPlaceholder: "Explain what CashMaker needs to correct",
        showCancelButton: true,
        confirmButtonText: "Return to CashMaker",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#b77900",
        allowOutsideClick: false,
        inputValidator: (value) =>
          !String(value || "").trim()
            ? "Please enter a return reason."
            : undefined
      });

      if (!confirmation.isConfirmed) return;

      const remark = String(
        confirmation.value || ""
      ).trim();

      const successfulIds = [];
      const failures = [];

      for (const entry of items) {
        try {
          const body = {
            action: "return",
            remark
          };

          if (role === "cashchecker") {
            await CashEntryServices.reviewByAdmin(
              entry._id,
              body
            );
          } else {
            await CashEntryServices.reviewByAccounts(
              entry._id,
              {
                ...body,
                bypassChecker: needsAccountsBypass(entry)
              }
            );
          }

          successfulIds.push(String(entry._id));
        } catch (error) {
          failures.push(
            `${entry.requestNumber || entry._id}: ${error?.response?.data?.message ||
            error?.message ||
            "Unable to return entry."
            }`
          );
        }
      }

      const successfulSet = new Set(successfulIds);

      setEntries((previous) =>
        previous.filter(
          (entry) =>
            !successfulSet.has(String(entry._id))
        )
      );

      setSelectedIds((previous) =>
        previous.filter(
          (id) => !successfulSet.has(String(id))
        )
      );

      setRefreshKey((value) => value + 1);

      await Swal.fire({
        icon: failures.length ? "warning" : "success",
        title: failures.length ? "Review Result" : "Returned",
        text: [
          `${successfulIds.length} entries returned to CashMaker.`,
          ...failures
        ].join("\n"),
        allowOutsideClick: false
      });
    } catch (error) {
      await Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to return entries.",
        "error"
      );
    } finally {
      actionLock.current = false;
      setProcessing(false);
    }
  };

  const reviewWithBypass = async (entry, action) => {
    if (
      role !== "accounts" ||
      loading ||
      actionLock.current ||
      entry.entryType !== "out" ||
      entry.status !== "pendingAdmin" ||
      !["approve", "reject"].includes(action)
    ) {
      return;
    }

    const isApproval = action === "approve";

    actionLock.current = true;
    setProcessing(true);

    try {
      const confirmation = await Swal.fire({
        title: isApproval
          ? "Bypass CashChecker & Approve?"
          : "Bypass CashChecker & Reject?",
        text:
          `Request ${entry.requestNumber || "—"} | ` +
          `${formatAmount(entry.amount)}. ` +
          (isApproval
            ? "This completes both approval stages and updates the passbook."
            : "Accounts will reject this entry. The passbook balance will remain unchanged."),
        icon: "warning",
        input: "textarea",
        inputLabel: isApproval
          ? "Bypass reason (required)"
          : "Bypass / rejection reason (required)",
        inputPlaceholder: isApproval
          ? "Enter the reason for bypassing CashChecker"
          : "Enter the reason for bypassing CashChecker and rejecting the entry",
        showCancelButton: true,
        confirmButtonText: isApproval
          ? "Bypass & Approve"
          : "Bypass & Reject",
        cancelButtonText: "Cancel",
        confirmButtonColor: isApproval ? "#198754" : "#dc3545",
        allowOutsideClick: false,
        inputValidator: (value) => {
          if (!String(value || "").trim()) {
            return isApproval
              ? "Please enter a bypass reason."
              : "Please enter a bypass / rejection reason.";
          }

          return undefined;
        }
      });

      if (!confirmation.isConfirmed) return;

      await CashEntryServices.reviewByAccounts(entry._id, {
        action,
        bypassChecker: true,
        remark: String(confirmation.value || "").trim()
      });

      const entryId = String(entry._id);

      setEntries((previous) =>
        previous.filter((item) => String(item._id) !== entryId)
      );

      setSelectedIds((previous) =>
        previous.filter((id) => id !== entryId)
      );

      setRefreshKey((value) => value + 1);

      await Swal.fire(
        isApproval ? "Approved" : "Rejected",
        isApproval
          ? "CashChecker stage bypassed. Entry approved and passbook updated."
          : "CashChecker stage bypassed. Entry rejected and passbook unchanged.",
        "success"
      );
    } catch (error) {
      setRefreshKey((value) => value + 1);

      await Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to complete the bypass review.",
        "error"
      );
    } finally {
      actionLock.current = false;
      setProcessing(false);
    }
  };

  const headers = [
    "#",
    "Request No/Details",
    "Date & Time",
    "Store / Location",
    "Cash Maker",
    "Passbook",
    "Category",
    "Amount",
    "Payment Mode",
    "Pending With"
  ];

  if (role === "accounts" && activeTab === "out") {
    headers.push("Action");
  }

  const thStyle = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#1565c0",
    color: "#ffffff",
    whiteSpace: "nowrap",
    fontSize: "12px",
    padding: "10px"
  };

  const tdStyle = {
    fontSize: "12px",
    padding: "10px",
    verticalAlign: "middle"
  };

  const columnCount = headers.length + (canReview ? 1 : 0);
  const busy = loading || processing;

  return (
    <>
      <Modal
        show={show}
        enforceFocus={false}
        onHide={closeModal}
        fullscreen
        scrollable
        backdrop={processing ? "static" : true}
        keyboard={!processing}
      >
        <Modal.Header closeButton={!processing}>
          <Modal.Title style={{ fontSize: "22px", fontWeight: 600 }}>
            Pending Cash Entries
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!allowed ? (
            <div className="text-center py-4">
              This role cannot access pending cash entries.
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge bg-success px-3 py-2">
                    Cash In: {cashInEntries.length}
                  </span>
                  <span className="badge bg-danger px-3 py-2">
                    Cash Out: {cashOutEntries.length}
                  </span>
                  <span className="badge bg-primary px-3 py-2">
                    Pending Amount: {formatAmount(pendingAmount)}
                  </span>
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setSelectedStore("");
                    setSearch("");
                    setRequestFlagFilter("");
                    setSelectedCashMaker([]);
                    setSelectedCategories([]);
                    setSelectedAmounts([]);
                    setPendingWithFilter("");
                    setFromDate("");
                    setToDate("");
                    setPageSize(100);
                    setPage(0);
                    setSelectedIds([]);

                    setRefreshKey((value) => value + 1);
                  }}
                >
                  <i className="fa fa-refresh me-2" aria-hidden="true" />
                  Refresh
                </Button>
              </div>

              <div className="small text-muted mb-3">
                {role === "accounts"
                  ? "Pending entries across all stores."
                  : "Pending entries from your assigned stores."}
                {canReview && (
                  <span className="ms-2">
                    You can review entries pending with{" "}
                    {statusLabels[reviewStatus]}.
                  </span>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm ${activeTab === "in" ? "btn-success" : "btn-outline-success"
                      }`}
                    disabled={busy}
                    onClick={() => setActiveTab("in")}
                    aria-pressed={activeTab === "in"}
                  >
                    Cash In ({cashInEntries.length})
                  </button>

                  <button
                    type="button"
                    className={`btn btn-sm ${activeTab === "out" ? "btn-danger" : "btn-outline-danger"
                      }`}
                    disabled={busy}
                    onClick={() => setActiveTab("out")}
                    aria-pressed={activeTab === "out"}
                  >
                    Cash Out ({cashOutEntries.length})
                  </button>
                </div>

                <div className="cash-pending-filters flex-shrink-0">
                  <select
                    className="form-control"
                    value={pageSize}
                    disabled={busy}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                    aria-label="Rows per page"
                  >
                    {[5, 10, 20, 50, 100].map((value) => (
                      <option key={value} value={value}>
                        {value} Rows
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row g-2 align-items-center mb-3 cash-pending-filters">
                <div className="col-12 col-md-3 col-xl-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search request / maker"
                    value={search}
                    disabled={busy}
                    onChange={(event) => setSearch(event.target.value)}
                    aria-label="Search cash entries"
                  />
                </div>

                <div className="col-12 col-md-3 col-xl-2">
                  <Select
                    options={storeOptions}
                    value={
                      storeOptions.find(
                        (option) => option.value === selectedStore
                      ) || storeOptions[0]
                    }
                    onChange={(selected) => {
                      setSelectedStore(selected?.value || "");
                      setSelectedCashMaker([]);
                      setSelectedCategories([]);
                      setSelectedAmounts([]);
                    }}
                    isSearchable
                    isDisabled={busy}
                    className="custom-react-select"
                    classNamePrefix="cash-pending-select"
                    menuPlacement="auto"
                    aria-label="Filter store"
                  />
                </div>

                <div className="col-12 col-md-2 col-xl-2">
                  <select
                    className="form-control"
                    value={requestFlagFilter}
                    disabled={busy}
                    onChange={(event) =>
                      setRequestFlagFilter(event.target.value)
                    }
                    aria-label="Filter request type"
                  >
                    <option value="">All Requests</option>
                    <option value="new">New</option>
                    <option value="resubmitted">
                      Returned &amp; Resubmitted
                    </option>
                    <option value="rejectedReprocess">
                      Rejected &amp; Reprocessed
                    </option>
                  </select>
                </div>

                <div className="col-12 col-md-2 col-xl-2">
                  <Select
                    isMulti
                    options={cashMakerOptions}
                    value={cashMakerOptions.filter((option) =>
                      selectedCashMaker.includes(option.value)
                    )}
                    onChange={(selected) =>
                      setSelectedCashMaker(
                        (selected || []).map((option) => option.value)
                      )
                    }
                    components={{
                      Option: CashMakerOption,
                      ValueContainer: CashMakerValueContainer
                    }}
                    placeholder="All Cash Makers"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    blurInputOnSelect={false}
                    isSearchable
                    isClearable
                    isDisabled={busy}
                    className="custom-react-select"
                    classNamePrefix="cash-pending-select"
                    menuPlacement="auto"
                    aria-label="Filter Cash Makers"
                  />
                </div>

                <div className="col-12 col-md-2 col-xl-2">
                  <Select
                    isMulti
                    options={categoryOptions}
                    value={categoryOptions.filter((option) =>
                      selectedCategories.includes(option.value)
                    )}
                    onChange={(selected) =>
                      setSelectedCategories(
                        (selected || []).map((option) => option.value)
                      )
                    }
                    components={{
                      Option: CashMakerOption,
                      ValueContainer: CashMakerValueContainer
                    }}
                    placeholder="All Categories"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    blurInputOnSelect={false}
                    isSearchable
                    isClearable
                    isDisabled={busy}
                    className="custom-react-select"
                    classNamePrefix="cash-pending-select"
                    menuPlacement="auto"
                    aria-label="Filter Categories"
                  />
                </div>

                <div className="col-12 col-md-2 col-xl-1">
                  <Select
                    isMulti
                    options={amountOptions}
                    value={amountOptions.filter((option) =>
                      selectedAmounts.includes(option.value)
                    )}
                    onChange={(selected) =>
                      setSelectedAmounts(
                        (selected || []).map((option) => option.value)
                      )
                    }
                    components={{
                      Option: CashMakerOption,
                      ValueContainer: CashMakerValueContainer
                    }}
                    placeholder="Amounts"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    blurInputOnSelect={false}
                    isSearchable
                    isClearable
                    isDisabled={busy}
                    className="custom-react-select"
                    classNamePrefix="cash-pending-select"
                    menuPlacement="auto"
                    aria-label="Filter Amounts"
                  />
                </div>

                <div className="col-12 col-md-2 col-xl-1">
                  <select
                    className="form-control"
                    value={pendingWithFilter}
                    disabled={busy}
                    onChange={(event) =>
                      setPendingWithFilter(event.target.value)
                    }
                    aria-label="Filter Pending With"
                    title="Pending With"
                  >
                    <option value="">Pending With: All</option>
                    <option value="pendingAdmin">CashChecker</option>
                    <option value="pendingAccounts">Accounts</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 justify-content-center align-items-end mb-3 cash-pending-filters">
                <div className="col-12 col-sm-5 col-md-3 col-xl-2">
                  <label
                    htmlFor="pending-cash-from-date"
                    className="form-label mb-1"
                  >
                    From Date
                  </label>

                  <input
                    id="pending-cash-from-date"
                    type="date"
                    className="form-control"
                    value={fromDate}
                    max={toDate || undefined}
                    disabled={busy}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFromDate(value);

                      if (value && toDate && value > toDate) {
                        setToDate("");
                      }
                    }}
                  />
                </div>

                <div className="col-12 col-sm-5 col-md-3 col-xl-2">
                  <label
                    htmlFor="pending-cash-to-date"
                    className="form-label mb-1"
                  >
                    To Date
                  </label>

                  <input
                    id="pending-cash-to-date"
                    type="date"
                    className="form-control"
                    value={toDate}
                    min={fromDate || undefined}
                    disabled={busy}
                    onChange={(event) => {
                      const value = event.target.value;
                      setToDate(value);

                      if (value && fromDate && value < fromDate) {
                        setFromDate("");
                      }
                    }}
                  />
                </div>

                {(fromDate || toDate) && (
                  <div className="col-12 col-sm-auto">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={busy}
                      onClick={() => {
                        setFromDate("");
                        setToDate("");
                      }}
                    >
                      Clear Dates
                    </button>
                  </div>
                )}
              </div>

              {canReview && selectedEntries.length > 0 && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <strong>Selected: {selectedEntries.length}</strong>

                    {bypassSelectedCount > 0 && (
                      <span className="badge bg-warning text-dark ms-3">
                        CashChecker bypass: {bypassSelectedCount}
                      </span>
                    )}
                    <span className="ms-3">
                      Amount: {formatAmount(selectedAmount)}
                    </span>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      disabled={busy}
                      onClick={() => reviewSelected("approve")}
                    >
                      Approve Selected ({selectedEntries.length})
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      onClick={() => reviewSelected("reject")}
                    >
                      Reject Selected ({selectedEntries.length})
                    </Button>
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      disabled={
                        busy ||
                        selectedEntries.length === 0 ||
                        selectedEntries.some(
                          (entry) =>
                            role === "cashchecker" &&
                            entry.entryType !== "out"
                        )
                      }
                      onClick={() =>
                        returnToCashMaker(selectedEntries)
                      }
                    >
                      Return to CashMaker ({selectedEntries.length})
                    </button>

                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => setSelectedIds([])}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}

              {processing && (
                <div className="text-primary mb-3" role="status">
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  />
                  Review in progress. Please wait.
                </div>
              )}

              <div
                className="scrolltable"
                style={{ maxHeight: "60vh", overflow: "auto" }}
              >
                <Table
                  bordered
                  hover
                  className="req-sticky-table"
                  style={{ marginBottom: 0 }}
                >
                  <thead className="sticky-table-header">
                    <tr>
                      {canReview && (
                        <th style={{ ...thStyle, textAlign: "center" }}>
                          <input
                            ref={selectPageRef}
                            type="checkbox"
                            checked={allPageSelected}
                            disabled={busy || pageIds.length === 0}
                            onChange={togglePage}
                            aria-label="Select reviewable entries on this page"
                          />
                        </th>
                      )}

                      {headers.map((label) => (
                        <th key={label} style={thStyle}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={columnCount}>
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ minHeight: 240 }}
                            role="status"
                            aria-label="Loading pending entries"
                          >
                            <Loader />
                          </div>
                        </td>
                      </tr>
                    ) : loadError ? (
                      <tr>
                        <td colSpan={columnCount} className="text-center py-4">
                          {loadError}
                          <button
                            type="button"
                            className="btn btn-primary btn-sm ms-2"
                            disabled={busy}
                            onClick={() =>
                              setRefreshKey((value) => value + 1)
                            }
                          >
                            Retry
                          </button>
                        </td>
                      </tr>
                    ) : visibleEntries.length === 0 ? (
                      <tr>
                        <td colSpan={columnCount} className="text-center py-4">
                          No pending Cash {activeTab === "in" ? "In" : "Out"}{" "}
                          entries found.
                        </td>
                      </tr>
                    ) : (
                      visibleEntries.map((entry, index) => (
                        <tr key={entry._id}>
                          {canReview && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedSet.has(String(entry._id))}
                                disabled={busy || !canSelectEntry(entry)}
                                onChange={() => toggleEntry(entry)}
                                aria-label={`Select entry ${entry.requestNumber || start + index + 1
                                  }`}
                                title={
                                  needsAccountsBypass(entry)
                                    ? "Select for Accounts action by bypassing CashChecker"
                                    : canSelectEntry(entry)
                                      ? "Select for review"
                                      : `Pending with ${statusLabels[entry.status] || "another role"}`
                                }
                              />
                            </td>
                          )}

                          <td style={tdStyle}>{start + index + 1}</td>
                          <td
                            style={{
                              ...tdStyle,
                              minWidth: "180px",
                              textAlign: "left",
                              overflowWrap: "anywhere"
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                              {entry.requestNumber || "—"}
                              <div className="mt-1 mb-1">
                                {entry.requestFlag === "rejectedReprocess" ? null :
                                  entry.requestFlag === "resubmitted" ||
                                    Number(entry.resubmissionCount || 0) > 0 ? (
                                    <span className="badge bg-warning text-dark">
                                      Returned &amp; Resubmitted
                                    </span>
                                  ) : (
                                    <span className="badge bg-info text-dark">
                                      New
                                    </span>
                                  )}
                              </div>
                            </div>

                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {entry.remark || "—"}
                            </div>

                            {entry.requestFlag === "rejectedReprocess" && (
                              <div className="mt-2">
                                <span className="badge bg-danger">
                                  Rejected &amp; Reprocessed
                                </span>

                                <div
                                  className="small mt-1"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  <strong>Reprocessing Reason: </strong>
                                  {entry.rejectionReprocessHistory?.slice(-1)[0]?.reason || "—"}
                                </div>
                              </div>
                            )}

                            {getEntryLogs(entry).length > 0 && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mt-2"
                                disabled={busy}
                                onClick={() => setLogEntry(entry)}
                              >
                                <i className="bi bi-clock-history me-1" />
                                View Log
                              </button>
                            )}
                            <div className="d-flex align-items-center gap-2 mt-2">
                              {getCashDocuments(entry).map((file, fileIndex) => (
                                <button
                                  key={`${file.url}-${fileIndex}`}
                                  type="button"
                                  disabled={busy}
                                  title={`Open attachment ${fileIndex + 1}`}
                                  aria-label={`Open attachment ${fileIndex + 1}`}
                                  onClick={() => {
                                    setDocumentIndex(fileIndex);
                                    setDocumentEntry(entry);
                                  }}
                                  style={{
                                    border: 0,
                                    background: "transparent",
                                    color: "#1565c0",
                                    padding: "2px 4px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    cursor: busy ? "default" : "pointer",
                                    fontSize: "14px"
                                  }}
                                >
                                  <i
                                    className={
                                      file.type === "pdf"
                                        ? "fa fa-file-pdf-o"
                                        : "fa fa-paperclip"
                                    }
                                    aria-hidden="true"
                                  />
                                  <span>{fileIndex + 1}</span>
                                </button>
                              ))}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                            {formatDate(entry.dateTime)}
                          </td>
                          <td style={tdStyle}>
                            <div
                              className="scrollable-description"
                              title={toInitCap(entry.store?.name)}
                            >
                              {toInitCap(entry.store?.name) || "—"}
                            </div>

                            <small>{toInitCap(entry.store?.location)}</small>
                          </td>
                          <td style={tdStyle}>
                            {toInitCap(entry.employee?.name) || "—"}
                          </td>
                          <td style={tdStyle}>
                            <div
                              className="scrollable-description"
                              title={toInitCap(entry.passbook?.name)}
                            >
                              {toInitCap(entry.passbook?.name) || "—"}
                            </div>
                          </td>
                          <td style={tdStyle}>
                            {entry.category?.name || "—"}
                          </td>
                          <td
                            className={
                              activeTab === "in"
                                ? "text-success fw-bold"
                                : "text-danger fw-bold"
                            }
                            style={{
                              ...tdStyle,
                              textAlign: "right",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {formatAmount(entry.amount)}
                          </td>
                          <td style={tdStyle}>
                            {entry.paymentMode === "cash"
                              ? "Cash"
                              : entry.paymentMode === "online"
                                ? "Online"
                                : "—"}
                          </td>


                          <td style={tdStyle}>
                            <span
                              className={`badge ${entry.status === "pendingAdmin"
                                ? "bg-warning text-dark"
                                : "bg-primary"
                                }`}
                            >
                              {entry.pendingWith ||
                                statusLabels[entry.status] ||
                                "—"}
                            </span>
                          </td>
                          {role === "accounts" && activeTab === "out" && (
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "center",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                minWidth: 150
                              }}
                            >
                              {entry.entryType === "out" &&
                                entry.status === "pendingAdmin" ? (
                                <div className="d-flex flex-column align-items-center gap-2">
                                  <span
                                    className="badge bg-warning text-dark"
                                    style={{ fontSize: 11 }}
                                  >
                                    Bypass CashChecker
                                  </span>

                                  <div
                                    className="d-flex justify-content-center align-items-center gap-2"
                                    role="group"
                                    aria-label="Bypass CashChecker actions"
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-outline-success btn-sm d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: 6
                                      }}
                                      disabled={busy}
                                      title="Approve"
                                      aria-label="Approve by bypassing CashChecker"
                                      onClick={() => reviewWithBypass(entry, "approve")}
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                        style={{ display: "block", flexShrink: 0 }}
                                      >
                                        <path d="M5 12l4 4L19 6" />
                                      </svg>
                                    </button>

                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: 6
                                      }}
                                      disabled={busy}
                                      title="Reject"
                                      aria-label="Reject by bypassing CashChecker"
                                      onClick={() => reviewWithBypass(entry, "reject")}
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        aria-hidden="true"
                                        style={{ display: "block", flexShrink: 0 }}
                                      >
                                        <path d="M6 6l12 12M18 6L6 18" />
                                      </svg>
                                    </button>

                                    <button
                                      type="button"
                                      className="btn btn-outline-warning btn-sm d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: 6
                                      }}
                                      disabled={busy}
                                      title="Return to CashMaker"
                                      aria-label="Return to CashMaker by bypassing CashChecker"
                                      onClick={() => returnToCashMaker([entry], true)}
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                        style={{ display: "block", flexShrink: 0 }}
                                      >
                                        <path d="M9 7l-5 5 5 5" />
                                        <path d="M4 12h10a6 6 0 006-6V4" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {!loading && !loadError && (
                <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                  <div className="dataTables_info">
                    Showing {filteredEntries.length ? start + 1 : 0} to{" "}
                    {Math.min(start + pageSize, filteredEntries.length)} of{" "}
                    {filteredEntries.length} entries
                  </div>

                  <Pagination
                    activePage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            disabled={processing}
            onClick={closeModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={show && Boolean(logEntry)}
        onHide={() => setLogEntry(null)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Request Log — {logEntry?.requestNumber || "—"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-info py-2">
            Latest saved return and resubmission events.
            Total resubmissions:{" "}
            {Number(logEntry?.resubmissionCount || 0)}
          </div>

          <div className="table-responsive">
            <Table bordered striped className="align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date &amp; Time</th>
                  <th>Action</th>
                  <th>By</th>
                  <th>Reason / Remark</th>
                </tr>
              </thead>

              <tbody>
                {getEntryLogs(logEntry).map((log, index) => (
                  <tr key={`${log.event}-${log.date}`}>
                    <td>{index + 1}</td>
                    <td className="text-nowrap">
                      {formatLogDate(log.date)}
                    </td>
                    <td>{log.event}</td>
                    <td>{log.by}</td>
                    <td
                      style={{
                        minWidth: 180,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere"
                      }}
                    >
                      {log.remark}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setLogEntry(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <CashDocumentPreviewModal
        show={show && Boolean(documentEntry)}
        handleClose={() => setDocumentEntry(null)}
        entry={documentEntry}
        initialIndex={documentIndex}
      />
    </>
  );
};

export default CashEntryPendingModal;